import { Tag, Typography } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import { formatVND } from '@/utils/format';
import type { FulfillmentMode, PosComboSearchItem } from '../types';

interface Props {
  item: PosComboSearchItem;
  fulfillmentMode: FulfillmentMode;
  onClick: () => void;
}

export function QRComboSuggestItem({ item, fulfillmentMode, onClick }: Props) {
  // PREORDER không có tồn để nói: `available_*` đều null, render ra sẽ thành
  // "tồn 0" + nhãn "Hết hàng" cho MỌI combo — thông tin sai hoàn toàn.
  const showStock = fulfillmentMode === 'INSTANT' && item.available_combo_qty !== null;
  const outOfStock = showStock && (item.available_combo_qty ?? 0) < 1;
  const savingPercent =
    item.retail_total > 0 ? Math.round((item.saving / item.retail_total) * 100) : 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        padding: 12,
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 12,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '';
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 60,
          height: 60,
          borderRadius: 4,
          background: '#fff1f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 26,
          color: '#a4193d',
        }}
      >
        <GiftOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text strong>{item.combo_name}</Typography.Text>
          {outOfStock && <Tag color="red">Hết hàng</Tag>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, marginBottom: 4 }}>
          <Tag color="purple">Combo</Tag>
          <Tag color="green">Giá: {formatVND(item.combo_price)}</Tag>
          {item.saving > 0 && (
            <Tag color="orange">
              Tiết kiệm {formatVND(item.saving)} · {savingPercent}%
            </Tag>
          )}
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          Mã combo: {item.combo_sku}
        </Typography.Text>
        {item.saving > 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Giá lẻ: <span style={{ textDecoration: 'line-through' }}>{formatVND(item.retail_total)}</span>
          </Typography.Text>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
          Gồm {item.component_items.length} sản phẩm:
        </Typography.Text>
        <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
          {item.component_items.map((component) => {
            const available = component.available_qty ?? 0;
            const missing = showStock ? Math.max(component.qty - available, 0) : 0;
            return (
              <li key={component.variant_id} style={{ fontSize: 12, color: missing > 0 ? '#cf1322' : '#8c8c8c' }}>
                {component.product_name} ×{component.qty}
                {showStock && ` — tồn ${available}`}
                {missing > 0 && ` ⚠ thiếu ${missing}`}
              </li>
            );
          })}
        </ul>
        {showStock && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
            Có thể bán: {item.available_combo_qty} combo
          </Typography.Text>
        )}
      </div>
    </div>
  );
}
